import {useMutation} from '@tanstack/react-query'
import {useIntl} from 'react-intl'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'

import {Error, Success} from 'src/feature/bite/helpers/notification'
import useRequestParams from 'src/feature/bite/hook/request'

const API_2 = process.env.REACT_APP_CDN_URL

export function useExportAll(type: number, orderList?: string) {
  const {auth, headers, errorMessage, signOut} = useRequestParams()

  const timezone = getTimeZone()

  const userID = auth()?.id

  const listFilter = {
    filterList: [],
    orderList: [
      {
        type: 2,
        // key: 'CreatedOn'
        key: orderList || 'CreatedOn',
      },
    ],
    paging: {
      currentPageNumber: 1,
      pageSize: 1000000,
    },
  }

  return useMutation(
    ['exportMembersAll', type],
    async (exportType: number) => {
      const Response = await fetch(`${API}/ExportFile/ExportFileCreate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          type,
          ...listFilter,
          userID,
          language: headers.Language,
          fileExportType: exportType,
          timeZoneOffSet: timezone,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errorMessage)
      },
    }
  )
}

export function useFileDownload() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()

  return useMutation(
    ['downloadFile'],
    async ({id, type, name}: {id: string; type: string; name}) => {
      const Response = await fetch(`${API_2}/FileDownload/DownloadFile`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth()?.id,
          language: headers.Language,
          fileID: id,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      if (Response.status !== 200) {
        Error(errorMessage)
        return false
      }

      // download blob file
      const Blob = await Response.blob()
      const Url = window.URL.createObjectURL(Blob)
      const Link = document.createElement('a')
      Link.href = Url
      Link.setAttribute('download', `${name}.${type}`)
      document.body.appendChild(Link)
      Link.click()
      document.body.removeChild(Link)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errorMessage)
      },
    }
  )
}

export function useFileUploadDownload() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()

  return useMutation(
    ['downloadUploadedFile'],
    async ({id, type, name}: {id: string; type: string; name: string}) => {
      const Response = await fetch(`${API_2}/FileDownload/DownloadUploadedFile`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth()?.id,
          language: headers.Language,
          fileID: id,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      if (Response.status !== 200) {
        Error(errorMessage)
        return false
      }

      // download blob file
      const Blob = await Response.blob()
      const Url = window.URL.createObjectURL(Blob)
      const Link = document.createElement('a')
      Link.href = Url
      Link.setAttribute('download', `${name}.${type}`)
      document.body.appendChild(Link)
      Link.click()
      document.body.removeChild(Link)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errorMessage)
      },
    }
  )
}

export function useImportProduct() {
  const intl = useIntl()
  const {auth, headers, signOut} = useRequestParams()

  const timezone = getTimeZone()

  return useMutation(['importProduct'], async ({file}: {file: File}) => {
    try {
      const myHeaders = new Headers()
      myHeaders.append('authorization', headers.Authorization)

      const formdata = new FormData()
      formdata.append('UserID', auth()?.id)
      formdata.append('File', file, file.name)
      formdata.append('language', headers.Language)
      formdata.append('timeZoneOffSet', timezone.toString())

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
      } as RequestInit

      const Response = await fetch(`${API}/StoreProduct/UploadProductFile`, requestOptions)

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      if (Response.status !== 200 || Data.responseResult.result === false) {
        Error(Data.responseResult.exceptionDetail)
        return false
      }

      Success(intl.formatMessage({id: 'GLOBAL.IMPORT.SUCCESS'}))

      return true
    } catch (error) {
      Error(intl.formatMessage({id: 'GLOBAL.IMPORT.ERROR'}))
    }
  })
}
